// components/branches/config/appointment/BookingWindowSettings.jsx
import React from "react";
import { CalendarIcon } from "@heroicons/react/outline";
import NumberInput from "../../../common/NumberInput";

const BookingWindowSettings = ({ config, onChange, isEditMode }) => {
  const handleChange = (field, value) => {
    onChange({
      ...config,
      [field]: value,
    });
  };

  const handleSameDayChange = (field, value) => {
    onChange({
      ...config,
      sameDayBooking: {
        ...config.sameDayBooking,
        [field]: value,
      },
    });
  };

  const handleAfterHoursChange = (field, value) => {
    onChange({
      ...config,
      afterHoursBooking: {
        ...config.afterHoursBooking,
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">
          Booking Window Configuration
        </h3>
        <p className="text-gray-600 mb-6">
          Control how far in advance customers can book appointments
        </p>
      </div>

      {/* Advance Booking */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h4 className="font-medium flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-blue-600" />
          Advance Booking Period
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Minimum Advance Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Standard Booking Window
            </label>
            {isEditMode ? (
              <div className="flex items-center gap-2">
                <NumberInput
                  value={config.advanceBookingDays || 14}
                  onChange={(value) =>
                    handleChange("advanceBookingDays", value)
                  }
                  min="1"
                  max="90"
                  className="w-24 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">days ahead</span>
              </div>
            ) : (
              <p className="text-lg font-semibold">
                {config.advanceBookingDays || 14} days
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              How many days in advance customers can book
            </p>
          </div>

          {/* Maximum Advance Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Advance Booking
            </label>
            {isEditMode ? (
              <div className="flex items-center gap-2">
                <NumberInput
                  value={config.maxAdvanceDays || 30}
                  onChange={(value) => handleChange("maxAdvanceDays", value)}
                  min="1"
                  max="365"
                  className="w-24 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">days maximum</span>
              </div>
            ) : (
              <p className="text-lg font-semibold">
                {config.maxAdvanceDays || 30} days
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Maximum days ahead customers can book
            </p>
          </div>
        </div>

        {/* Minimum Advance Notice */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Advance Notice
          </label>
          {isEditMode ? (
            <div className="flex items-center gap-2">
              <NumberInput
                value={config.minAdvanceHours || 2}
                onChange={(value) => handleChange("minAdvanceHours", value)}
                min="0"
                max="48"
                className="w-24 focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">hours before</span>
            </div>
          ) : (
            <p className="text-lg font-semibold">
              {config.minAdvanceHours || 2} hours
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Minimum time required between booking and appointment
          </p>
        </div>
      </div>

      {/* Same-Day Booking */}
      <div className="bg-blue-50 rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-medium">Same-Day Booking</h4>
            <p className="text-sm text-gray-600 mt-1">
              Allow customers to book appointments for today
            </p>
          </div>
          {isEditMode ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.sameDayBooking?.enabled || false}
                onChange={(e) =>
                  handleSameDayChange("enabled", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          ) : (
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                config.sameDayBooking?.enabled
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {config.sameDayBooking?.enabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </div>

        {config.sameDayBooking?.enabled && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Same-Day Booking Cutoff Time
            </label>
            {isEditMode ? (
              <input
                type="time"
                value={config.sameDayBooking?.cutoffTime || "15:00"}
                onChange={(e) =>
                  handleSameDayChange("cutoffTime", e.target.value)
                }
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="font-semibold">
                {config.sameDayBooking?.cutoffTime || "15:00"}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              No same-day bookings allowed after this time
            </p>
          </div>
        )}
      </div>

      {/* After-Hours Booking */}
      <div className="bg-purple-50 rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-medium">After-Hours Booking</h4>
            <p className="text-sm text-gray-600 mt-1">
              Allow booking appointments outside business hours
            </p>
          </div>
          {isEditMode ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.afterHoursBooking?.enabled || false}
                onChange={(e) =>
                  handleAfterHoursChange("enabled", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          ) : (
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                config.afterHoursBooking?.enabled
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {config.afterHoursBooking?.enabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </div>

        {config.afterHoursBooking?.enabled && (
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="afterHoursApproval"
              checked={config.afterHoursBooking?.requiresApproval || false}
              onChange={(e) =>
                handleAfterHoursChange("requiresApproval", e.target.checked)
              }
              disabled={!isEditMode}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="afterHoursApproval" className="text-sm">
              Require manual approval for after-hours appointments
            </label>
          </div>
        )}
      </div>

      {/* Visual Timeline */}
      <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
        <h4 className="font-medium mb-4">Booking Timeline Visual</h4>
        <div className="relative">
          <div className="h-2 bg-gray-200 rounded-full"></div>
          <div
            className="absolute top-0 left-0 h-2 bg-blue-500 rounded-full"
            style={{ width: "50%" }}
          ></div>

          <div className="flex justify-between mt-4 text-sm">
            <div className="text-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mx-auto mb-1"></div>
              <p className="text-xs text-gray-600">
                Min: {config.minAdvanceHours || 2}h
              </p>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mb-1"></div>
              <p className="text-xs text-gray-600">
                Standard: {config.advanceBookingDays || 14}d
              </p>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-1"></div>
              <p className="text-xs text-gray-600">
                Max: {config.maxAdvanceDays || 30}d
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingWindowSettings;
